<script setup lang="ts">
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaAuthStatus from '@/components/design/domain/auth/GaiaAuthStatus.vue';
import {
  clearIntegrationOauthContext,
  INTEGRATION_OAUTH_SCOPE_KEY,
  readIntegrationOauthContext,
  type IntegrationOauthProvider,
  type IntegrationOauthScope,
} from '@/services/integration/integrationOauthService';
import { completeGoogleDriveOAuth } from '@/services/integration/googleDriveIntegrationService';
import { completeSlackOAuth } from '@/services/integration/slackIntegrationService';
import { completeJiraOAuth } from '@/services/integration/jiraIntegrationService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGaiaToast } from '@/composables/useGaiaToast';

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();

const processing = ref(true);

function queryValue(key: string): string {
  const value = route.query[key];
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
}

function routeProvider(): IntegrationOauthProvider {
  const provider = String(route.params.provider).toLowerCase();
  if (provider === 'google_drive' || provider === 'slack' || provider === 'jira') {
    return provider;
  }

  const contextProvider = readIntegrationOauthContext()?.provider;
  if (
    contextProvider === 'google_drive' ||
    contextProvider === 'slack' ||
    contextProvider === 'jira'
  ) {
    return contextProvider;
  }

  return 'google_drive';
}

/**
 * The CSRF check for Jira: Atlassian echoes back the `state` we generated before the redirect,
 * and the frontend is the only place it is ever compared. A missing or mismatched value means
 * the callback did not originate from a flow we started, so the code must not be posted.
 */
function hasVerifiedJiraState(): boolean {
  const expected = readIntegrationOauthContext()?.state;
  const returned = queryValue('state');
  return Boolean(expected) && returned === expected;
}

function resolveScope(): IntegrationOauthScope {
  const queryScope = queryValue('scope').toLowerCase();
  if (queryScope === 'user' || queryScope === 'organisation') {
    return queryScope;
  }

  const contextScope = readIntegrationOauthContext()?.scope;
  if (contextScope === 'user' || contextScope === 'organisation') {
    return contextScope;
  }

  const stored = window.sessionStorage.getItem(INTEGRATION_OAUTH_SCOPE_KEY);
  if (stored === 'user' || stored === 'organisation') {
    return stored;
  }

  return 'organisation';
}

async function completeOauth(
  provider: IntegrationOauthProvider,
  code: string,
  scope: IntegrationOauthScope
): Promise<unknown> {
  if (provider === 'slack') {
    return completeSlackOAuth(code);
  }
  return completeGoogleDriveOAuth(code, scope);
}

async function redirectAfterOauth(params: {
  provider: IntegrationOauthProvider;
  success: boolean;
  scope: IntegrationOauthScope;
  /** Overrides the reported status on success — Jira lands on `pending` until a site is chosen. */
  status?: string;
  error?: string;
}): Promise<void> {
  const context = readIntegrationOauthContext();
  const origin = context?.origin ?? 'integrations_org';
  const outcome = params.success
    ? { status: params.status ?? 'connected' }
    : { error: params.error ?? 'OAuth failed' };

  if (origin === 'profile_user') {
    await router.replace({
      name: 'settings-profile',
      query: {
        integration: params.provider,
        scope: params.scope,
        ...outcome,
      },
    });
    return;
  }

  await router.replace({
    name: 'service-account-token',
    query: {
      integration: params.provider,
      scope: params.scope,
      ...outcome,
    },
  });
}

onMounted(async () => {
  const provider = routeProvider();
  const code = queryValue('code');
  const oauthError = queryValue('error');
  const scope = resolveScope();

  try {
    if (oauthError) {
      // `error_description` can carry provider internals, so it only goes to the logs.
      const description = queryValue('error_description');
      if (description) {
        logger.error(`OAuth callback error for ${provider}: ${oauthError} - ${description}`);
      }
      message.error(`OAuth failed: ${oauthError}`);
      await redirectAfterOauth({ provider, success: false, scope, error: oauthError });
      return;
    }

    // No code and no error is a malformed callback (bookmark, refresh, double-visit) — send the
    // user back rather than posting an empty code. Checked before the state comparison so those
    // visits do not read as a CSRF failure.
    if (!code) {
      await redirectAfterOauth({ provider, success: false, scope, error: 'Missing OAuth code.' });
      return;
    }

    if (provider === 'jira' && !hasVerifiedJiraState()) {
      const detail = 'Could not verify the Jira connection request. Please start again.';
      message.error(detail);
      await redirectAfterOauth({ provider, success: false, scope, error: detail });
      return;
    }

    if (provider === 'jira') {
      // The code is single-use and expires in ~10 minutes: post it once, never retry.
      const status = await completeJiraOAuth(code);
      await redirectAfterOauth({ provider, success: true, scope, status: status.state });
      return;
    }

    await completeOauth(provider, code, scope);
    await redirectAfterOauth({ provider, success: true, scope });
  } catch (error) {
    const detail = extractErrorDetail(error, 'Failed to complete OAuth.');
    message.error(detail);
    await redirectAfterOauth({ provider, success: false, scope, error: detail });
  } finally {
    clearIntegrationOauthContext();
    processing.value = false;
  }
});
</script>

<template>
  <!-- A popup that closes itself, so AuthLayout drops the brand panel here
       (meta.authBrand: false) and puts the mark above the card instead. -->
  <gaia-auth-card>
    <gaia-auth-status :loading="processing" tone="success" title="Connected">
      <template #icon>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 10.5 8.2 14.5 16 6" />
        </svg>
      </template>
      <template v-if="processing">You'll be sent back to settings in a moment.</template>
      <template v-else>Taking you back to settings…</template>
    </gaia-auth-status>
  </gaia-auth-card>
</template>
